import {AxiosInstance} from 'axios'
import MyAxiosInstanceCarona from './axiosInstanceCarona'
import {
    AtaDetalhe,
    BuscaRecente,
    BuscaRequest,
    BuscaResponse,
    CriarJustificativaRequest,
    DocumentoProcessoOut,
    DocumentoRequest,
    IaTextoOut,
    IaTextoRequest,
    IrpResumo,
    JustificativaOut,
    JustificativaResumo,
    RascunhoOut,
    RascunhoRequest,
    SugestaoAlternativa,
    SugestaoDemandaOut,
    SugestoesAlternativasRequest,
    VinculoPesquisaOut,
} from '../pages/CaronaInteligente/types'

/**
 * Teto de espera das sugestões/comparação on-demand (chamada SÍNCRONA).
 *
 * A instância axios do módulo não define `timeout` — o default do axios é 0
 * (SEM limite), então uma conexão pendurada deixaria o botão girando para
 * sempre. O backend derruba a própria chamada em ~26s, logo 30s cobre o pior
 * caso legítimo e qualquer coisa além disso é estouro de verdade.
 */
export const SUGESTOES_ALTERNATIVAS_TIMEOUT_MS = 120_000

/**
 * Cliente do carona-service. Busca e detalhe são gratuitos; gerar justificativa
 * custa 1 documento (cobrança server-side; o 406 sinaliza "sem créditos").
 */
export class CaronaApiService {
    private api: AxiosInstance

    constructor() {
        this.api = MyAxiosInstanceCarona
    }

    async buscar(req: BuscaRequest): Promise<BuscaResponse> {
        const {data} = await this.api.post('/atas/buscar', req)
        return data
    }

    /** numero_controle tem barras (rota :path) — NÃO encodar as "/". Com `termo`
     * (a busca original do usuário), cada item volta com `aderente: true` quando é
     * o RECOMENDADO para essa busca (sem termo, aderente=false). */
    async detalhe(
        numeroControle: string,
        esferaAderente?: string,
        termo?: string
    ): Promise<AtaDetalhe> {
        const parts: string[] = []
        if (esferaAderente) parts.push(`esfera_aderente=${encodeURIComponent(esferaAderente)}`)
        if (termo && termo.trim()) parts.push(`termo=${encodeURIComponent(termo.trim())}`)
        const query = parts.length ? `?${parts.join('&')}` : ''
        const {data} = await this.api.get(
            `/atas/${numeroControle}${query}`
        )
        return data
    }

    /** 202 + processamento assíncrono. Lança no 406 (sem créditos). */
    async criarJustificativa(
        req: CriarJustificativaRequest
    ): Promise<JustificativaOut> {
        const {data} = await this.api.post('/justificativas', req)
        return data
    }

    async obterJustificativa(id: string): Promise<JustificativaOut> {
        const {data} = await this.api.get(`/justificativas/${id}`)
        return data
    }

    /** Cria um RASCUNHO (entrevista salva pela metade) → 201 RascunhoOut. */
    async salvarRascunho(req: RascunhoRequest): Promise<RascunhoOut> {
        const {data} = await this.api.post('/justificativas/rascunhos', req)
        return data
    }

    /** Atualiza um rascunho (autosave/pausa) → 200 RascunhoOut. INCREMENTAL: só os
     * campos presentes são gravados. 404 = inexistente/outra org; 409 = já concluído
     * (não volta a rascunho). */
    async atualizarRascunho(id: string, req: RascunhoRequest): Promise<RascunhoOut> {
        const {data} = await this.api.put(`/justificativas/rascunhos/${id}`, req)
        return data
    }

    /** Retoma um rascunho → 200 RascunhoOut (respostas/evidencias/estado_parcial). */
    async obterRascunho(id: string): Promise<RascunhoOut> {
        const {data} = await this.api.get(`/justificativas/rascunhos/${id}`)
        return data
    }

    /** Histórico "Minhas justificativas". */
    async listarJustificativas(): Promise<JustificativaResumo[]> {
        const {data} = await this.api.get('/justificativas')
        return data
    }

    /** "Suas últimas buscas" do usuário (home da busca) — até 8, recentes primeiro. */
    async buscasRecentes(): Promise<BuscaRecente[]> {
        const {data} = await this.api.get('/atas/buscas-recentes')
        return data || []
    }

    /** Vincula a Pesquisa de Preços (IN 65) criada a partir da justificativa
     * (handoff do módulo — mesmo contrato do Levantamento de Mercado).
     * Idempotente: re-vincular substitui o vínculo. */
    async vincularPesquisa(
        justificativaId: string,
        pesquisaId: string
    ): Promise<VinculoPesquisaOut> {
        const {data} = await this.api.post(
            `/justificativas/${justificativaId}/vincular-pesquisa`,
            {pesquisa_precos_id: pesquisaId}
        )
        return data
    }

    /** Sugestões de termo (IA via n8n) quando a busca vem vazia. */
    async sugestoes(termo: string): Promise<string[]> {
        const {data} = await this.api.get(`/atas/sugestoes?termo=${encodeURIComponent(termo)}`)
        return (data && data.sugestoes) || []
    }

    /** Typeahead do campo de busca: objetos sugeridos a partir do PNCP. */
    async autocomplete(q: string): Promise<string[]> {
        const {data} = await this.api.get(`/atas/autocomplete?q=${encodeURIComponent(q)}`)
        return (data && data.sugestoes) || []
    }

    /** IRPs abertas que casam com o objeto (trilho "Participar"; do nosso banco). */
    async buscarIrp(termo: string, uf?: string): Promise<IrpResumo[]> {
        const q = new URLSearchParams({termo})
        if (uf) q.set('uf', uf)
        const {data} = await this.api.get(`/irp?${q.toString()}`)
        return (data && data.resultados) || []
    }

    /** Sugere a redação da "demanda concreta" (entrevista) via IA — 202 + processamento
     * assíncrono (o resultado chega pelo notifier; ver obterSugestaoDemanda). */
    async sugerirDemanda(objeto: string, esfera: string, orgao: string): Promise<SugestaoDemandaOut> {
        const {data} = await this.api.post('/justificativas/sugerir-demanda', {objeto, esfera, orgao})
        return data
    }

    async obterSugestaoDemanda(id: string): Promise<SugestaoDemandaOut> {
        const {data} = await this.api.get(`/justificativas/sugestoes-demanda/${id}`)
        return data
    }

    /** Upload do ETP/DFD/TR (PDF/DOCX ≤10MB): o backend extrai o texto na hora e o
     * documento vira contexto opcional da IA na justificativa (documento_id). */
    async enviarDocumento(file: File): Promise<DocumentoProcessoOut> {
        const form = new FormData()
        form.append('file', file)
        const {data} = await this.api.post('/justificativas/documentos', form, {
            headers: {'Content-Type': 'multipart/form-data'},
        })
        return data
    }

    /** Documentos de planejamento já enviados pelo usuário (org-scoped). */
    async listarDocumentos(): Promise<DocumentoProcessoOut[]> {
        const {data} = await this.api.get('/justificativas/documentos')
        return Array.isArray(data) ? data : []
    }

    /** Dispara a redação da justificativa pela Licito.guru IA — assíncrono: 202
     * {status: PROCESSANDO} e o resultado chega por push (notifier) + GET de
     * detalhe. 409 = já há redação em andamento; 200 CONCLUIDO = já pronta. */
    async redigirJustificativa(req: IaTextoRequest): Promise<IaTextoOut> {
        const {data} = await this.api.post('/justificativas/ia-texto', req)
        return data
    }

    /** Sugestões ON-DEMAND da Licito.guru IA para o quadro de análise de alternativas
     * do ETP. SÍNCRONO (pode levar ~18s; não cobra). Body: numero_controle da ata
     * escolhida + (opcional) os numero_controle das alternativas exibidas — sem
     * preço, o backend re-coleta. 404 se a ata escolhida não estiver no cache;
     * devolve [] quando não há comparáveis. Timeout EXPLÍCITO (ver
     * SUGESTOES_ALTERNATIVAS_TIMEOUT_MS): sem ele o axios espera para sempre. O
     * estouro chega como AxiosError code ECONNABORTED — a tela mostra mensagem
     * amigável e libera o botão. */
    async sugestoesAlternativas(
        req: SugestoesAlternativasRequest
    ): Promise<SugestaoAlternativa[]> {
        const {data} = await this.api.post(
            '/justificativas/sugestoes-alternativas',
            req,
            {timeout: SUGESTOES_ALTERNATIVAS_TIMEOUT_MS}
        )
        return (data && data.sugestoes_alternativas) || []
    }

    /** Gera o documento (docx | pdf) a partir do conteúdo (editável) e devolve o blob. */
    async gerarDocumento(req: DocumentoRequest, formato: 'docx' | 'pdf' = 'docx'): Promise<Blob> {
        const {data} = await this.api.post(
            `/justificativas/documento?formato=${formato}`, req, {responseType: 'blob'}
        )
        return data
    }
}

export const caronaApi = new CaronaApiService()
